import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Megaphone, Plus, Search, ShieldAlert, X } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { useCampaignListQuery } from '@/client/api/campaigns/lifecycle.query';
import {
  CAMPAIGN_STATUS_BADGE,
  CampaignListParams,
  CHANNEL_LABEL,
  formatEpoch,
} from '@/client/types/campaigns';
import { useListFilters } from '@/client/hooks/useListFilters';
import { useBreadcrumbs } from '@/client/hooks/use-breadcrumbs';
import { getApiErrorMessage } from '@/client/utils/errors';
import { useHasPermission } from '@/client/hooks/useHasPermission';
import Pagination from '@/client/components/common/Pagination';

const AccessDenied = () => (
  <div className="flex flex-col items-center justify-center gap-3 rounded-xl border border-gray-100 bg-white py-20 text-center shadow-sm">
    <div className="flex h-14 w-14 items-center justify-center rounded-full bg-red-50">
      <ShieldAlert className="h-7 w-7 text-red-500" />
    </div>
    <h2 className="text-lg font-semibold text-gray-800">Access restricted</h2>
    <p className="max-w-sm text-sm text-gray-500">
      Viewing campaigns requires{' '}
      <code className="rounded bg-gray-100 px-1.5 py-0.5 text-xs">campaign.view</code>.
    </p>
  </div>
);

interface Filters {
  status: string;
  channel: string;
}

const CampaignListPage = () => {
  useBreadcrumbs([
    { label: 'Campaigns', href: '/admin/relationship/campaigns' },
    { label: 'All campaigns' },
  ]);

  const viewPerm = useHasPermission('campaign.view');
  const createPerm = useHasPermission('campaign.create');
  const canView = viewPerm.isSuperAdmin || viewPerm.hasPermission;
  const canCreate = createPerm.isSuperAdmin || createPerm.hasPermission;
  const permLoading = viewPerm.isLoading;

  const {
    searchTerm,
    filters,
    page,
    limit,
    handleSearch,
    handleFilterChange,
    handlePageChange,
    handleLimitChange,
    resetFilters,
  } = useListFilters<Filters>({
    initialFilters: {
      status: '',
      channel: '',
    },
    initialLimit: 25,
  });

  const listParams: CampaignListParams = useMemo(
    () => ({
      limit,
      offset: (page - 1) * limit,
      ...(filters.status ? { status: filters.status } : {}),
      ...(filters.channel ? { channel: filters.channel } : {}),
    }),
    [filters, page, limit],
  );

  const { data, isLoading, isError, error } = useCampaignListQuery(listParams, canView && !permLoading);
  const campaigns = (data?.campaigns ?? []).filter((c) => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return true;
    return (
      c.name?.toLowerCase().includes(q) ||
      c.campaign_id?.toLowerCase().includes(q) ||
      c.status?.toLowerCase().includes(q)
    );
  });
  const pagination = data?.pagination;

  if (permLoading) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="mx-auto max-w-screen-2xl space-y-4">
          <Skeleton className="h-10 w-48" />
          <Skeleton className="h-64 w-full" />
        </div>
      </div>
    );
  }

  if (!canView) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="mx-auto max-w-screen-2xl">
          <AccessDenied />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="mx-auto max-w-screen-2xl space-y-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="mb-1 flex items-center gap-2.5">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-600">
                <Megaphone className="h-4 w-4 text-white" />
              </div>
              <h1 className="text-2xl font-black text-gray-900">Campaigns</h1>
            </div>
            <p className="text-sm text-gray-500">
              Draft → schedule → run across email, SMS, WhatsApp, and in-app. Live progress refreshes
              while running.
            </p>
          </div>
          {canCreate && (
            <Button asChild className="gap-2">
              <Link to="/admin/relationship/campaigns/new">
                <Plus className="h-4 w-4" />
                New campaign
              </Link>
            </Button>
          )}
        </div>

        <div className="rounded-lg bg-white p-4 shadow">
          <div className="flex flex-col gap-3 sm:flex-row">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search name or id…"
                value={searchTerm}
                onChange={(e) => handleSearch(e.target.value)}
                className="w-full rounded-md border border-gray-300 py-2 pl-9 pr-4 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm"
            >
              <option value="">All statuses</option>
              <option value="draft">Draft</option>
              <option value="scheduled">Scheduled</option>
              <option value="running">Running</option>
              <option value="paused">Paused</option>
              <option value="completed">Completed</option>
              <option value="canceled">Canceled</option>
              <option value="failed">Failed</option>
            </select>
            <select
              value={filters.channel}
              onChange={(e) => handleFilterChange('channel', e.target.value)}
              className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm"
            >
              <option value="">All channels</option>
              <option value="email">Email</option>
              <option value="sms">SMS</option>
              <option value="whatsapp">WhatsApp</option>
              <option value="in_app">In-app</option>
            </select>
            {(searchTerm || filters.status || filters.channel) && (
              <button
                type="button"
                onClick={resetFilters}
                className="inline-flex items-center gap-1 rounded-md border border-gray-200 px-3 py-2 text-sm text-gray-600 hover:bg-gray-50"
              >
                <X className="h-4 w-4" />
                Clear
              </button>
            )}
          </div>
        </div>

        <div className="overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm">
          {isLoading && (
            <div className="space-y-3 p-4">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          )}
          {isError && (
            <p className="p-8 text-center text-sm text-red-600">
              {getApiErrorMessage(error, 'Failed to load campaigns.')}
            </p>
          )}
          {!isLoading && !isError && campaigns.length === 0 && (
            <div className="p-12 text-center">
              <Megaphone className="mx-auto mb-3 h-12 w-12 text-gray-300" />
              <p className="text-sm text-gray-500">No campaigns yet.</p>
              {canCreate && (
                <Button asChild className="mt-4 gap-2">
                  <Link to="/admin/relationship/campaigns/new">
                    <Plus className="h-4 w-4" />
                    Create draft
                  </Link>
                </Button>
              )}
            </div>
          )}
          {!isLoading && campaigns.length > 0 && (
            <div className="overflow-x-auto">
              <table className="min-w-full text-left text-sm">
                <thead className="border-b bg-gray-50 text-xs uppercase text-gray-500">
                  <tr>
                    <th className="px-4 py-3 font-medium">Campaign</th>
                    <th className="px-4 py-3 font-medium">Status</th>
                    <th className="px-4 py-3 font-medium">Channels</th>
                    <th className="px-4 py-3 font-medium">Progress</th>
                    <th className="px-4 py-3 font-medium">Updated</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {campaigns.map((c) => {
                    const p = c.progress;
                    return (
                      <tr key={c.campaign_id} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <Link
                            to={`/admin/relationship/campaigns/${c.campaign_id}`}
                            className="font-medium text-blue-700 hover:underline"
                          >
                            {c.name}
                          </Link>
                          <p className="font-mono text-xs text-gray-400">
                            {c.campaign_id.slice(0, 12)}…
                          </p>
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
                              CAMPAIGN_STATUS_BADGE[c.status] || 'bg-gray-100 text-gray-700'
                            }`}
                          >
                            {c.status}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-gray-600">
                          {(c.channels || [])
                            .map((ch) => CHANNEL_LABEL[ch] || ch)
                            .join(', ') || '—'}
                        </td>
                        <td className="px-4 py-3 text-gray-600">
                          {p
                            ? `${p.sent ?? 0}/${p.total ?? '—'} sent · ${p.failed ?? 0} failed`
                            : '—'}
                        </td>
                        <td className="whitespace-nowrap px-4 py-3 text-gray-500">
                          {formatEpoch(c.updated_at || c.created_at)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          {pagination && (
            <div className="border-t border-gray-100 px-4 py-3">
              <Pagination
                total={pagination.total ?? campaigns.length}
                page={page}
                itemsPerPage={limit}
                limit={limit}
                setPage={handlePageChange}
                setItemsPerPage={handleLimitChange}
                showButtons
                showSelect
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CampaignListPage;
